import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'

import { DateTimePicker } from '../../lib/DatePicker'
import { formatDateTime, nowThai } from '../../lib/datetime'
import { canEdit } from '../../lib/nav'
import { Pager } from '../../lib/Pager'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import {
  WINDOW_STATE_LABEL,
  errorText,
  minuteKey,
  useOffsite,
  windowState,
} from '../../lib/offsite'
import type { OffsiteSite, OffsiteWindow, WindowState } from '../../lib/offsite'
import { useAuthedSession } from '../../lib/SessionContext'
import { ReasonPrompt, SectionHead, ViewerNote } from './parts'

/** ปุ่มลัด — คนกดหน้านี้ตอนเครื่องสแกนหน้าเสียกำลังรีบ ไม่ควรต้องนั่งคำนวณเวลาสิ้นสุด */
const QUICK_HOURS = [2, 4, 8]

const STATE_CHIP: Record<WindowState, string> = {
  open: 'chip chip--ok',
  upcoming: 'chip',
  expired: 'chip chip--off',
  cancelled: 'chip chip--off',
}

function siteText(siteId: number | null, siteMap: Map<number, OffsiteSite>): string {
  if (siteId === null) return 'สำนักงานใหญ่'
  const site = siteMap.get(siteId)
  return site ? `${site.code} · ${site.name}` : `#${siteId}`
}

export default function OffsiteWindows() {
  const session = useAuthedSession()
  const offsite = useOffsite()
  const writable = canEdit(session)

  const [windows, setWindows] = useState<OffsiteWindow[] | null>(null)
  /** สถานะ "ตอนนี้เปิดอยู่ไหม" ที่ backend คำนวณจากนาฬิกา DB — ถือเป็นคำตอบจริง */
  const [current, setCurrent] = useState<OffsiteWindow | null>(null)
  const [sites, setSites] = useState<OffsiteSite[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loadedAt, setLoadedAt] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [cancelling, setCancelling] = useState<OffsiteWindow | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const sort = useSort([{ key: 'startsAt', dir: 'desc' }], () => setPage(1))
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // status=all เพราะหน้านี้ต้องโชว์ประวัติรวมที่ยกเลิกแล้วด้วย
      // (`current` มาในผลลัพธ์เดียวกัน ไม่ต้องยิงสองรอบ)
      const res = await offsite.listWindows('all', { page, pageSize, sort: sort.param })
      setWindows(res.windows)
      setTotal(res.total)
      setCurrent(res.current)
      setLoadedAt(nowThai())
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [offsite, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let alive = true
    offsite
      .listSites()
      .then((rows) => {
        if (alive) setSites(rows)
      })
      .catch(() => {
        // ไม่ได้ชื่อสถานที่ก็ยังอ่านตารางได้ (โชว์เป็น #id) ไม่ต้องล้มทั้งหน้า
      })
    return () => {
      alive = false
    }
  }, [offsite, page, pageSize])

  const siteMap = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites])
  const headOffice = sites.find((s) => s.status === 'active' && Boolean(s.isHeadOffice)) ?? null
  const nowKey = minuteKey(nowThai())

  return (
    <>
      <h1>เปิดลงเวลาชั่วคราว</h1>

      {/* แถบสถานะบนสุด = สิ่งที่คนเปิดหน้านี้มาอยากรู้ที่สุด อยู่ก่อนทุกอย่าง
          ⚠️ ระหว่างยังโหลดไม่เสร็จ current เป็น null เหมือนกรณี "ไม่มีช่วงเวลาเปิด" ต้องแยกให้ออก
             ไม่งั้นหน้าจะขึ้นว่าปิดอยู่ทั้งที่ยังไม่รู้คำตอบ ซึ่งอ่านผิดแล้วไปเปิดซ้ำได้ */}
      {windows === null ? (
        <p className="meta">
          {loadError ? 'ตรวจสอบสถานะปัจจุบันไม่ได้ — ดูข้อความข้างล่าง' : 'กำลังตรวจสอบสถานะปัจจุบัน…'}
        </p>
      ) : current ? (
        <div className="warn">
          <strong>กำลังเปิดให้ลงเวลาชั่วคราวอยู่</strong>
          <dl>
            <dt>ถึงเวลา</dt>
            <dd>{formatDateTime(current.endsAt)}</dd>
            <dt>เริ่มเมื่อ</dt>
            <dd>{formatDateTime(current.startsAt)}</dd>
            <dt>เหตุผล</dt>
            <dd>{current.reason}</dd>
            <dt>ขอบเขต</dt>
            <dd>{siteText(current.siteId, siteMap)}</dd>
            <dt>ผู้เปิด</dt>
            <dd>{current.createdBy ?? '-'}</dd>
          </dl>
        </div>
      ) : (
        <p className="ok">
          <strong>ไม่มีช่วงเวลาเปิดอยู่</strong> — ตอนนี้ลงเวลาผ่าน LINE ได้เฉพาะคนที่มีใบอนุญาตรายคน
        </p>
      )}

      {/* เขียนไว้ตรงนี้เพราะเป็นเรื่องที่คนเปิดเข้าใจผิดบ่อยที่สุด */}
      <p className="meta">
        เปิดแล้ว <strong>ทุกคนลงเวลาผ่าน LINE ได้ แต่ยังต้องอยู่ในรัศมีของสถานที่</strong>{' '}
        ไม่ใช่เปิดให้ลงเวลาจากบ้าน — เพราะเหตุที่ต้องเปิด (เครื่องสแกนหน้าเสีย)
        หมายถึงคนมาถึงที่ทำงานแล้ว · ถ้าต้องการให้ใครลงเวลาจากที่อื่นได้จริง ให้ออกใบอนุญาตแบบ
        “จากที่ใดก็ได้” ที่เมนูสิทธิ์ลงเวลานอกสถานที่แทน
      </p>

      {!writable && <ViewerNote />}
      {loadError && <p className="error">โหลดข้อมูลไม่สำเร็จ — {loadError}</p>}
      {notice && <p className="ok">{notice}</p>}
      {sites.length > 0 && !headOffice && (
        <p className="warn">
          ยังไม่ได้กำหนดสำนักงานใหญ่ — ถ้าเปิดโดยไม่ระบุสถานที่ ระบบจะหาขอบเขตไม่ได้
          และพนักงานยังลงเวลาไม่ได้ ให้ตั้งที่เมนู “สถานที่ลงเวลา” ก่อน
        </p>
      )}

      {writable && !showForm && (
        <p>
          <button type="button" onClick={() => setShowForm(true)}>
            เปิดช่วงเวลาใหม่
          </button>
        </p>
      )}

      {writable && showForm && (
        <WindowForm
          sites={sites}
          headOffice={headOffice}
          onOpened={(message) => {
            setShowForm(false)
            setNotice(message)
            void load()
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {cancelling && (
        <ReasonPrompt
          title="ยกเลิกช่วงเวลาที่เปิดไว้"
          detail={`${formatDateTime(cancelling.startsAt)} – ${formatDateTime(cancelling.endsAt)} · ${cancelling.reason}`}
          confirmLabel="ยกเลิกช่วงเวลานี้"
          placeholder="เช่น ซ่อมเครื่องสแกนเสร็จแล้ว"
          onConfirm={async (reason) => {
            await offsite.cancelWindow(cancelling.id, reason)
            setCancelling(null)
            setNotice('ยกเลิกช่วงเวลาแล้ว')
            await load()
          }}
          onClose={() => setCancelling(null)}
        />
      )}

      <section className="card">
        <SectionHead
          title="ประวัติการเปิด"
          meta={loadedAt ? `ข้อมูลเมื่อ ${loadedAt.slice(11, 16)} (เวลาไทย)` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        {windows === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : windows.length === 0 ? (
          <p className="meta">ยังไม่เคยเปิดช่วงเวลาให้ลงเวลาชั่วคราว</p>
        ) : (
          <>
            <SortBar
              sort={sort.sort}
              labels={{ startsAt: 'เริ่ม', endsAt: 'สิ้นสุด', reason: 'เหตุผล', createdBy: 'ผู้เปิด', status: 'สถานะ' }}
              onClear={() => sort.setSort([])}
            />
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <SortTh label="เริ่ม" sortKey="startsAt" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                    <SortTh label="สิ้นสุด" sortKey="endsAt" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                    <SortTh label="เหตุผล" sortKey="reason" sort={sort.sort} onToggle={sort.toggle} />
                    {/* ขอบเขตแสดงเป็นชื่อสถานที่ที่ join มาจากอีกตาราง — เรียงตาม siteId ไม่สื่อ */}
                    <th>ขอบเขต</th>
                    <SortTh label="ผู้เปิด" sortKey="createdBy" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                    {writable && <th>จัดการ</th>}
                  </tr>
                </thead>
                <tbody>
                  {windows.map((w) => {
                    // ถ้า backend บอกว่าอันนี้คือช่วงที่เปิดอยู่ ให้เชื่อ backend
                    // (นาฬิกาเครื่องที่เปิดหน้านี้อาจไม่ตรง หรือหน้าเปิดค้างไว้ข้ามวัน)
                    const state: WindowState = current?.id === w.id ? 'open' : windowState(w, nowKey)
                    return (
                      <tr key={w.id} className={state === 'open' ? undefined : 'off'}>
                        <td className="nowrap">{formatDateTime(w.startsAt)}</td>
                        <td className="nowrap">{formatDateTime(w.endsAt)}</td>
                        <td>{w.reason}</td>
                        <td>{siteText(w.siteId, siteMap)}</td>
                        <td className="nowrap">{w.createdBy ?? '-'}</td>
                        <td>
                          <span className={STATE_CHIP[state]}>{WINDOW_STATE_LABEL[state]}</span>
                          {state === 'cancelled' && (
                            <div className="meta">
                              {w.cancelReason ?? '-'}
                              {w.cancelledBy ? ` · ${w.cancelledBy}` : ''}
                            </div>
                          )}
                        </td>
                        {writable && (
                          <td>
                            {/* ยกเลิกได้เฉพาะอันที่ยังมีผลข้างหน้า — อันที่หมดเวลาแล้วยกเลิกไปก็ไม่มีความหมาย */}
                            {(state === 'open' || state === 'upcoming') && (
                              <button
                                type="button"
                                className="ghost tiny"
                                onClick={() => setCancelling(w)}
                              >
                                ยกเลิก
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPage={setPage}
          unit="ช่วง"
          onPageSize={(n) => {
            setPageSize(n)
            setPage(1)
          }}
        />
      </section>
    </>
  )
}

function WindowForm({
  sites,
  headOffice,
  onOpened,
  onCancel,
}: {
  sites: OffsiteSite[]
  headOffice: OffsiteSite | null
  onOpened: (message: string) => void
  onCancel: () => void
}) {
  const offsite = useOffsite()

  // ตั้งต้นเป็น "ตอนนี้" เพราะเคสจริงคือของเสียอยู่ตรงหน้าแล้วต้องเปิดเดี๋ยวนี้
  const [startsAt, setStartsAt] = useState(() => nowThai())
  const [endsAt, setEndsAt] = useState('')
  const [siteId, setSiteId] = useState('')
  const [reason, setReason] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activeSites = sites.filter((s) => s.status === 'active')
  const rangeBad = startsAt !== '' && endsAt !== '' && endsAt <= startsAt
  const canSubmit =
    !submitting && startsAt !== '' && endsAt !== '' && !rangeBad && reason.trim().length > 0

  /** เปิดจาก "เดี๋ยวนี้" ถึงอีก n ชั่วโมง */
  function quick(hours: number) {
    setStartsAt(nowThai())
    setEndsAt(nowThai(hours))
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return

    setSubmitting(true)
    setError(null)
    try {
      await offsite.createWindow({
        /* ⚠️ ส่งค่าดิบจาก DateTimePicker (`YYYY-MM-DDTHH:mm`) ขึ้นไปตรง ๆ
           **ห้าม** new Date(startsAt).toISOString() — คอลัมน์ starts_at/ends_at เก็บ
           "เวลาไทย" ไม่ใช่ UTC ถ้าแปลงเป็น ISO เวลาจะถอยไป 7 ชั่วโมง กลายเป็นเปิดช่วงที่
           ผ่านไปแล้ว (กรอก 09:00 จะได้ 02:00) แล้วพนักงานจะลงเวลาไม่ได้ทั้งที่หน้าจอบอกว่าเปิดแล้ว */
        startsAt,
        endsAt,
        siteId: siteId === '' ? null : Number(siteId),
        reason: reason.trim(),
      })
      onOpened('เปิดช่วงเวลาให้ลงเวลาชั่วคราวแล้ว')
    } catch (err) {
      setError(errorText(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="card form-card--wide">
      <h2>เปิดช่วงเวลาใหม่</h2>
      <form onSubmit={submit}>
        <div>
          <div className="row-actions">
            {QUICK_HOURS.map((hours) => (
              <button key={hours} type="button" className="ghost" onClick={() => quick(hours)}>
                เปิด {hours} ชั่วโมงจากนี้
              </button>
            ))}
          </div>
          <small className="hint">กดปุ่มลัดแล้วเวลาเริ่ม/สิ้นสุดจะถูกเติมให้ ปรับเองภายหลังได้</small>
        </div>

        <div className="form-row">
          <label>
            เวลาเริ่ม
            <DateTimePicker value={startsAt} onChange={setStartsAt} />
          </label>
          <label>
            เวลาสิ้นสุด
            <DateTimePicker value={endsAt} onChange={setEndsAt} min={startsAt} />
            {rangeBad && <small className="bad">เวลาสิ้นสุดต้องหลังเวลาเริ่ม</small>}
          </label>
        </div>
        <small className="hint">
          เวลาที่กรอกเป็น <strong>เวลาไทย</strong> ตามที่นาฬิกาบนผนังบอก (ระบบไม่แปลง timezone ให้)
        </small>

        <label>
          เหตุผล (บังคับกรอก)
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={255}
            placeholder="เช่น เครื่องสแกนหน้าชั้น 1 เสีย รอช่างวันนี้"
            required
          />
          <small className="hint">
            ช่วงเวลาที่เปิดไว้เป็นข้อยกเว้น — เหตุผลจะถูกเก็บไว้ในประวัติให้ตรวจย้อนหลังได้
          </small>
        </label>

        <label>
          ขอบเขตสถานที่
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">
              สำนักงานใหญ่{headOffice ? ` (${headOffice.code} · ${headOffice.name})` : ' — ยังไม่ได้กำหนด!'}
            </option>
            {activeSites.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.code} · {s.name} (รัศมี {s.radiusM} ม.)
              </option>
            ))}
          </select>
          <small className="hint">
            พนักงานต้องอยู่ในรัศมีของสถานที่นี้ตอนกดลงเวลา · เว้นไว้ = ใช้สำนักงานใหญ่
          </small>
        </label>

        {error && <p className="error">{error}</p>}

        <div className="row-actions">
          <button type="submit" disabled={!canSubmit}>
            {submitting ? 'กำลังบันทึก…' : 'เปิดช่วงเวลานี้'}
          </button>
          <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
            ยกเลิก
          </button>
        </div>
      </form>
    </section>
  )
}
